import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  useAddUserSocialIntegrationsMutation,
  useLazyGetUserSocialIntegrationsForAdditionQuery,
  useLazyGetUserSocialIntegrationsQuery,
  useRemoveUserSocialIntegrationsMutation,
} from '@app/api/Users';
import { SUCCESS } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';

export const useUserIntegrationEffects = () => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddUserIntegrationsModalOpen, setIsAddUserIntegrationsModalOpen] =
    useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [selectedAddedIntegrations, setSelectedAddedIntegrations] = useState<
    number[]
  >([]);

  const [searchParams] = useSearchParams();
  const userId = Number(searchParams.get('id'));

  const { activeBranchId } = useUserPrint();
  const { showNotification } = useContext(NotificationContext);

  const [removeUserSocialIntegrations] =
    useRemoveUserSocialIntegrationsMutation();
  const [addUserSocialIntegrations] = useAddUserSocialIntegrationsMutation();

  const [
    handleGetUserSocialIntegrations,
    { isLoading: isGetUserSocialIntegrationsLoading, data: integrations },
  ] = useLazyGetUserSocialIntegrationsQuery();
  const [
    handleGetUserSocialIntegrationsForAddition,
    {
      isLoading: isAvailableIntegrationsForAdditionLoading,
      data: availableIntegrationsForAddition,
    },
  ] = useLazyGetUserSocialIntegrationsForAdditionQuery();

  const handleDeleteIntegration = async () => {
    if (!activeBranchId || !userId || isNaN(userId) || !selectedRowKeys) return;

    try {
      await removeUserSocialIntegrations({
        ActiveBranchId: activeBranchId,
        data: {
          UserId: userId,
          IntegrationIds: selectedRowKeys.map(Number),
        },
      }).unwrap();

      showNotification('Удаление выполнено успешно', SUCCESS);
    } catch (error) {
      showNotification(error as Error);
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedRowKeys([]);

      await handleGetUserSocialIntegrations({
        ActiveBranchId: activeBranchId,
        forUserId: userId,
      });

      await handleGetUserSocialIntegrationsForAddition({
        ActiveBranchId: activeBranchId,
        forUserId: userId,
      });
    }
  };

  const handleAddIntegrations = async (addedIntegrations: number[]) => {
    if (!activeBranchId || !userId || isNaN(userId) || !addedIntegrations)
      return;

    try {
      await addUserSocialIntegrations({
        ActiveBranchId: activeBranchId,
        data: {
          UserId: userId,
          IntegrationIds: addedIntegrations,
        },
      }).unwrap();

      showNotification('Добавление выполнено успешно', SUCCESS);
    } catch (error) {
      showNotification(error as Error);
    } finally {
      setIsAddUserIntegrationsModalOpen(false);
      setSelectedAddedIntegrations([]);

      await handleGetUserSocialIntegrations({
        ActiveBranchId: activeBranchId,
        forUserId: userId,
      });

      await handleGetUserSocialIntegrationsForAddition({
        ActiveBranchId: activeBranchId,
        forUserId: userId,
      });
    }
  };

  useEffect(() => {
    if (!userId || isNaN(userId) || !activeBranchId) return;

    handleGetUserSocialIntegrations({
      ActiveBranchId: activeBranchId,
      forUserId: userId,
    });
  }, [activeBranchId, handleGetUserSocialIntegrations, userId]);

  useEffect(() => {
    if (isAddUserIntegrationsModalOpen) {
      if (
        !activeBranchId ||
        !userId ||
        isNaN(userId) ||
        availableIntegrationsForAddition
      )
        return;

      handleGetUserSocialIntegrationsForAddition({
        ActiveBranchId: activeBranchId,
        forUserId: userId,
      });
    }
  }, [
    activeBranchId,
    handleGetUserSocialIntegrationsForAddition,
    isAddUserIntegrationsModalOpen,
    userId,
    availableIntegrationsForAddition,
  ]);

  const handleCloseModal = () => {
    setIsAddUserIntegrationsModalOpen(false);
    setSelectedAddedIntegrations([]);
  };

  return {
    integrations,
    isGetUserSocialIntegrationsLoading,
    availableIntegrationsForAddition,
    isAvailableIntegrationsForAdditionLoading,
    handleDeleteIntegration,
    handleAddIntegrations,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    isAddUserIntegrationsModalOpen,
    setIsAddUserIntegrationsModalOpen,
    selectedRowKeys,
    setSelectedRowKeys,
    selectedAddedIntegrations,
    setSelectedAddedIntegrations,
    handleCloseModal,
  };
};
